import React from "react";
import {
  Badge,
  Box,
  Button,
  Header,
  Link,
  Table,
  TableProps,
} from "@amzn/awsui-components-react";
import {
  Investigation,
  InvestigationTrendPoint,
  Stage,
  UninvestigatedGroup,
} from "../../lusca-search-api/generated-src";
import {
  DEFAULT_GROUPS_QUERY,
  GroupsQuery,
  useUninvestigatedGroups,
} from "../../hooks/useInvestigations";
import { seedFromGroup } from "./InvestigationBacklog";
import { TrendSparkline } from "./TrendSparkline";
import { formatGrowth, trendGrowth } from "./trendUtils";
import { RowSeed, investigationPath, isTerminal } from "./investigationUtils";
import { OneLine } from "../common/OneLine";

export const GROWING_LIMIT = 5;
export const GROWING_TITLE = "Growing fastest";

/** Backlog page the section reads: category view, biggest 7-day growth first. */
export const GROWING_GROUPS_QUERY: GroupsQuery = {
  ...DEFAULT_GROUPS_QUERY,
  byCategory: true,
  sort: "growth7d",
  desc: true,
  limit: GROWING_LIMIT,
  offset: 0,
};

/** One row of the section: an active investigation or a group nobody is on. */
export interface GrowingRow {
  key: string;
  kind: "investigation" | "backlog";
  label: string;
  detail: string;
  items: number;
  growth: number;
  trend?: InvestigationTrendPoint[];
  investigation?: Investigation;
  group?: UninvestigatedGroup;
}

const groupLabel = (
  g: UninvestigatedGroup
): { label: string; detail: string } => {
  const category = g.errorCategory || "uncategorized";
  if (category !== "uncategorized") {
    const queues = new Set((g.members ?? []).map((m) => m.eventsourcekey)).size;
    return {
      label: category,
      detail:
        queues > 1
          ? `${queues} queues`
          : g.eventsourcekey || g.members?.[0]?.eventsourcekey || "",
    };
  }
  return {
    label: g.errorType || "Uncategorized group",
    detail: g.eventsourcekey || "",
  };
};

/**
 * Populations that grew over the last 7 days of snapshots, investigated or not, biggest growth
 * first. Shrinking or flat populations and ones seen in a single snapshot are left out.
 */
export function growingRows(
  investigations: Investigation[],
  groups: UninvestigatedGroup[],
  limit = GROWING_LIMIT
): GrowingRow[] {
  const rows: GrowingRow[] = [];
  investigations.forEach((inv) => {
    if (isTerminal(inv.status)) return;
    const growth = trendGrowth(inv.trend);
    if (growth === null || growth <= 0) return;
    rows.push({
      key: `inv:${inv.id}`,
      kind: "investigation",
      label: inv.title,
      detail: inv.owner ? `Owner ${inv.owner}` : "Needs an owner",
      items: inv.assignedCount ?? 0,
      growth,
      trend: inv.trend,
      investigation: inv,
    });
  });
  groups.forEach((g) => {
    const growth = g.growth7d ?? trendGrowth(g.trend);
    if (growth === null || growth === undefined || growth <= 0) return;
    const { label, detail } = groupLabel(g);
    rows.push({
      key: `grp:${[
        g.errorCategory,
        g.eventsourcekey,
        g.errorType,
        g.errorMessage,
      ]
        .map((v) => v ?? "")
        .join("|")}`,
      kind: "backlog",
      label,
      detail,
      items: g.count ?? 0,
      growth,
      trend: g.trend,
      group: g,
    });
  });
  return rows
    .sort((a, b) => b.growth - a.growth || b.items - a.items)
    .slice(0, limit);
}

/** Column definitions, built outside the component so cells are not new component types. */
function growingColumns(
  stage: Stage,
  onOpenInvestigation: (investigationId: string) => void,
  onStartFromGroup: (seed: RowSeed) => void
): TableProps.ColumnDefinition<GrowingRow>[] {
  return [
    {
      id: "name",
      header: "Population",
      cell: (row) => {
        const investigationId = row.investigation?.id;
        return (
          <div style={{ overflowWrap: "anywhere" }}>
            {investigationId ? (
              <Link
                href={investigationPath(investigationId, stage)}
                onFollow={(e) => {
                  e.preventDefault();
                  onOpenInvestigation(investigationId);
                }}
              >
                {row.label}
              </Link>
            ) : (
              row.label
            )}
            {row.detail && (
              <Box variant="small" color="text-body-secondary" display="block">
                {row.detail}
              </Box>
            )}
          </div>
        );
      },
    },
    {
      id: "kind",
      header: "",
      width: 150,
      cell: (row) =>
        row.kind === "backlog" ? (
          <Badge color="red">Not investigated</Badge>
        ) : (
          <Badge color="blue">Investigation</Badge>
        ),
    },
    {
      id: "items",
      header: "Items",
      width: 100,
      cell: (row) => (
        <Box textAlign="right">
          <OneLine>{row.items.toLocaleString()}</OneLine>
        </Box>
      ),
    },
    {
      id: "trend",
      header: "7-day trend",
      width: 140,
      cell: (row) => <TrendSparkline trend={row.trend} />,
    },
    {
      id: "action",
      header: "",
      width: 170,
      cell: (row) => {
        const { group } = row;
        return group ? (
          <Button
            variant="inline-link"
            ariaLabel={`Start investigation: ${row.label} (${formatGrowth(
              row.growth
            )} in 7 days)`}
            onClick={() => onStartFromGroup(seedFromGroup(group))}
          >
            Start investigation
          </Button>
        ) : null;
      },
    },
  ];
}

interface InvestigationGrowingProps {
  stage: Stage;
  investigations: Investigation[];
  investigationsLoading: boolean;
  onOpenInvestigation: (investigationId: string) => void;
  onStartFromGroup: (seed: RowSeed) => void;
}

export const InvestigationGrowing: React.FC<InvestigationGrowingProps> = ({
  stage,
  investigations,
  investigationsLoading,
  onOpenInvestigation,
  onStartFromGroup,
}) => {
  const groups = useUninvestigatedGroups(stage, false, GROWING_GROUPS_QUERY);
  const loading = investigationsLoading || groups.isLoading;
  const rows = growingRows(
    investigations,
    groups.data?.available === false ? [] : groups.data?.groups ?? []
  );
  if (!loading && !rows.length) return null;

  return (
    <Table
      columnDefinitions={growingColumns(
        stage,
        onOpenInvestigation,
        onStartFromGroup
      )}
      items={rows}
      trackBy="key"
      loading={loading && !rows.length}
      loadingText="Loading trends"
      variant="container"
      wrapLines
      header={
        <Header
          variant="h3"
          description="Biggest item increase over the last 7 days of DLQ snapshots, investigated or not."
        >
          {GROWING_TITLE}
        </Header>
      }
    />
  );
};

export default InvestigationGrowing;
